import {Vector2, Vector3} from 'three';

import {Future} from 'external/dev_april_corgi+/js/common/futures';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {CorgiEvent, DOM_MOUSE} from 'external/dev_april_corgi+/js/corgi/events';
import {ViewsService} from 'external/dev_april_corgi+/js/corgi/history/views_service';
import {DialogService} from 'external/dev_april_corgi+/js/emu/dialog';
import {CHANGED} from 'external/dev_april_corgi+/js/emu/events';

import {GetCurrentUserResponse} from 'petri/service_pb';

import {GRAPH_NOT_YET_SAVED_ID} from './conventions';
import {GraphController} from './graph_controller';
import {ADD_NODE_MENU} from './graph_element';
import {makeInventoryEditor} from './inventory_editor';
import {getLabwareSettings, setLabwareSettings} from './types/labware_settings';
import {makeLabwareSettingsEditor} from './labware_settings_editor';
import {Divider, Menu, MenuItem} from './menu_controller';
import {MenuService} from './menu_service';
import {makeOpenGraphDialog} from './open_graph_dialog';
import {fireAndForget} from './promises';
import {Routes} from './route_controller';
import {getInventory, setInventory} from './types/custom';
import {
  deleteGraph,
  exportWorkspace,
  importWorkspace,
  listGraphs,
  loadGraphData,
  saveGraph,
} from './workspace_storage';

interface Args {
  graphId: string;
}

export interface State {
  currentUser: Future<GetCurrentUserResponse>;
  graphName: string;
  saveState: 'clean' | 'dirty' | 'saving';
}

type Deps = typeof GraphEditorController.deps;

export class GraphEditorController extends Controller<
  Args,
  Deps,
  HTMLElement,
  State
> {
  static deps() {
    return {
      controllers: {
        graph: GraphController,
      },
      services: {
        dialog: DialogService,
        views: ViewsService<Routes>,
        menu: MenuService,
      },
    };
  }

  private readonly graphController: GraphController;
  private readonly dialog: DialogService;
  private readonly graphId: string | typeof GRAPH_NOT_YET_SAVED_ID;
  private readonly views: ViewsService<Routes>;
  private readonly menu: MenuService;

  constructor(response: Response<GraphEditorController>) {
    super(response);
    this.graphController = response.deps.controllers.graph;
    this.dialog = response.deps.services.dialog;
    this.graphId = response.args.graphId;
    this.views = response.deps.services.views;
    this.menu = response.deps.services.menu;

    // Register keydown on the document body so we can catch it. We can't use corgi because it will
    // preventDefault on the event and block browser behaviors.
    this.registerListener(document.body, 'keydown', e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        fireAndForget(this.save());
      }
    });
    this.registerListener(window, 'beforeunload', e => {
      if (this.state.saveState !== 'clean') {
        e.preventDefault();
        e.returnValue = true;
      }
    });
  }

  wakeup(): void {
    if (this.graphId === GRAPH_NOT_YET_SAVED_ID) {
      fireAndForget(
        this.updateState({
          ...this.state,
          graphName: 'Untitled',
        }),
      );
    } else {
      fireAndForget(this.loadGraph());
    }
  }

  getGraphController(): GraphController {
    return this.graphController;
  }

  graphDirtied(): void {
    this.updateState({
      ...this.state,
      saveState: 'dirty',
    });
  }

  fileMenuClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const target = e.actionElement.element() as HTMLElement;
    const rect = target.getBoundingClientRect();

    const items: Array<Divider | Menu | MenuItem> = [
      {
        kind: 'menu_item',
        label: 'New',
        action: () => fireAndForget(this.newGraph()),
      },
      {
        kind: 'menu_item',
        label: 'Open...',
        action: () => fireAndForget(this.openGraph()),
      },
      {
        kind: 'menu_item',
        label: 'Save',
        action: () => fireAndForget(this.save()),
      },
      {
        kind: 'menu_item',
        label: 'Duplicate',
        action: () => fireAndForget(this.duplicateGraph()),
      },
      {
        kind: 'menu_item',
        label: 'Delete',
        action: () => fireAndForget(this.deleteCurrentGraph()),
      },
      {kind: 'divider'},
      {
        kind: 'menu_item',
        label: 'Import Workspace...',
        action: () => this.importWorkspaceFromFile(),
      },
      {
        kind: 'menu_item',
        label: 'Export Workspace',
        action: () => this.exportWorkspaceToFile(),
      },
    ];

    this.openMenuDropdown(items, rect);
  }

  addMenuClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const target = e.actionElement.element() as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.openMenuDropdown(ADD_NODE_MENU, rect);
  }

  toolsMenuClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const target = e.actionElement.element() as HTMLElement;
    const rect = target.getBoundingClientRect();

    const items: Array<Divider | Menu | MenuItem> = [
      {
        kind: 'menu_item',
        label: 'Simulate',
        action: () => fireAndForget(this.graphController.simulateRun()),
      },
      {kind: 'divider'},
      {
        kind: 'menu_item',
        label: 'Edit inventory',
        action: () => this.editInventory(),
      },
      {
        kind: 'menu_item',
        label: 'Edit labware',
        action: () => this.editLabwareSettings(),
      },
    ];

    this.openMenuDropdown(items, rect);
  }

  private openMenuDropdown(
    items: Array<Divider | Menu | MenuItem>,
    rect: DOMRect,
  ): void {
    // Get the center of the current viewport as the position for new nodes
    const camera = this.graphController.getCamera();
    const context = {
      controller: this.graphController,
      graph: this.graphController.graph,
      position: new Vector3(camera.x, camera.y, 0),
    };

    this.menu.open(
      context,
      items,
      new Vector2(rect.left, rect.bottom),
      this.root,
      {anchor: 'top_left'},
    );
  }

  async newGraph(): Promise<void> {
    if (this.state.saveState === 'dirty') {
      if (!confirm('You have unsaved changes. Create a new graph anyway?')) {
        return;
      }
    }

    this.views.goTo('/graphs/' + GRAPH_NOT_YET_SAVED_ID);
  }

  async openGraph(): Promise<void> {
    if (this.state.saveState === 'dirty') {
      if (
        !confirm('You have unsaved changes. Open a different graph anyway?')
      ) {
        return;
      }
    }

    const graphs = listGraphs();
    if (graphs.length === 0) {
      alert('No saved graphs found.');
      return;
    }

    const onSelect = (id: string) => {
      this.views.goTo('/graphs/' + id);
    };

    this.dialog
      .display(
        makeOpenGraphDialog({graphs, currentGraphId: this.graphId, onSelect}),
      )
      .then(
        () => {},
        () => {},
      );
  }

  async save(): Promise<void> {
    await this.updateState({
      ...this.state,
      saveState: 'saving',
    });

    const dataGz = await this.graphController.graph.dump();
    const graphId = await saveGraph(
      this.graphId === GRAPH_NOT_YET_SAVED_ID ? undefined : this.graphId,
      this.state.graphName,
      dataGz,
      this.graphController.graph.version(),
    );

    // If this was a new graph, update the URL to reflect the saved ID
    if (this.graphId === GRAPH_NOT_YET_SAVED_ID) {
      this.views.goTo('/graphs/' + graphId);
    } else {
      await this.updateState({
        ...this.state,
        saveState: 'clean',
      });
    }
  }

  async duplicateGraph(): Promise<void> {
    const dataGz = await this.graphController.graph.dump();
    const newName = this.state.graphName + ' (copy)';
    const graphId = await saveGraph(
      undefined, // New ID
      newName,
      dataGz,
      this.graphController.graph.version(),
    );

    this.views.goTo('/graphs/' + graphId);
  }

  async deleteCurrentGraph(): Promise<void> {
    if (!this.graphId) {
      alert('This graph has not been saved yet.');
      return;
    }

    if (!confirm(`Delete "${this.state.graphName}"? This cannot be undone.`)) {
      return;
    }

    deleteGraph(this.graphId);

    this.views.goTo('/');
  }

  rename(e: CorgiEvent<typeof CHANGED>): void {
    const name = e.detail.value;
    this.graphController.graph.setName(name);
    this.updateState({
      ...this.state,
      graphName: name,
      saveState: 'dirty',
    });
  }

  importWorkspaceFromFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        importWorkspace(text);
        alert('Workspace imported successfully.');
        this.views.goTo('/');
      } catch (e) {
        alert(
          'Failed to import workspace: ' +
            (e instanceof Error ? e.message : String(e)),
        );
      }
    };
    input.click();
  }

  exportWorkspaceToFile(): void {
    const json = exportWorkspace();
    const blob = new Blob([json], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'petri-workspace.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  editInventory(): void {
    const inventory = getInventory();
    this.dialog
      .display(
        makeInventoryEditor({
          inventory,
          onSave: newInventory => {
            setInventory(newInventory);
          },
        }),
      )
      .then(
        () => {},
        () => {},
      );
  }

  editLabwareSettings(): void {
    const settings = getLabwareSettings();
    this.dialog
      .display(
        makeLabwareSettingsEditor({
          settings,
          onSave: newSettings => {
            setLabwareSettings(newSettings);
          },
        }),
      )
      .then(
        () => {},
        () => {},
      );
  }

  private async loadGraph(): Promise<void> {
    const graphId = this.graphId;
    if (!graphId) return;

    const data = loadGraphData(graphId);
    if (!data) return;

    await this.graphController.graph.load(data.name, data.bytes, data.version);
    await this.updateState({
      ...this.state,
      graphName: data.name,
      saveState: 'clean',
    });
  }
}
